<script lang="ts">
  import Button from '../../Button.svelte';
  import { ProjectContentType, type ProjectComment } from '$shared/lib';
  import type { User } from 'src/routes/(app)/+layout.server';
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { client } from '$lib/api/Client';
  import { projectCommentsQueryKey } from '$lib/pages/Projects/HackathonProjectComments.svelte';
  import type { ListProjectCommentsResponse } from '$shared/api';
  import ThumbsUpIcon from '$icons/ThumbsUpIcon.svelte';

  export let comment: ProjectComment;
  export let currentUser: User | null;

  const queryClient = useQueryClient();
  const likeMutation = createMutation({
    mutationFn: async (comment: ProjectComment) => {
      if (comment.likedByCurrentUser) {
        return client.projects.deleteProjectLike({
          likableContentType: ProjectContentType.COMMENT,
          contentId: comment.id,
        });
      }

      return client.projects.createProjectLike({
        likedContentType: ProjectContentType.COMMENT,
        contentId: comment.id,
      });
    },
    onMutate: async (comment: ProjectComment) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: projectCommentsQueryKey(comment.projectId),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<{
        pages: ListProjectCommentsResponse[];
      }>(projectCommentsQueryKey(comment.projectId));

      // Optimistically update the comment
      if (previousData) {
        const updateCommentInTree = (comments: ProjectComment[]): ProjectComment[] => {
          return comments.map((c) => {
            if (c.id === comment.id) {
              return {
                ...c,
                likedByCurrentUser: !c.likedByCurrentUser,
                likeCount: c.likedByCurrentUser ? c.likeCount - 1 : c.likeCount + 1,
              };
            }
            if (c.replies?.length) {
              return { ...c, replies: updateCommentInTree(c.replies) };
            }
            return c;
          });
        };

        queryClient.setQueryData<{ pages: ListProjectCommentsResponse[] }>(
          projectCommentsQueryKey(comment.projectId),
          {
            ...previousData,
            pages: previousData.pages.map((page) => ({
              ...page,
              comments: updateCommentInTree(page.comments),
            })),
          },
        );
      }

      return { previousData };
    },
    onError: (err, comment, context) => {
      // Roll back to the previous state if there's an error
      if (context?.previousData) {
        queryClient.setQueryData(projectCommentsQueryKey(comment.projectId), context.previousData);
      }
    },
  });

  async function updateLike(comment: ProjectComment) {
    await $likeMutation.mutateAsync(comment);
  }
</script>

<Button
  size="sm"
  class="!px-0 {comment.likedByCurrentUser ? 'text-mint-dark-11' : ''}"
  transparent={!comment.likedByCurrentUser}
  disabled={!currentUser}
  on:click={() => updateLike(comment)}
>
  <ThumbsUpIcon />
  {#if comment.likeCount > 0}
    <span>{comment.likeCount}</span>
  {:else}
    <span>Like</span>
  {/if}
</Button>
